import { useState, useEffect, useContext } from "react";
import { Trophy, Coins, Gem, Crown, Medal, Star, Users, RefreshCw, ChevronRight, Gamepad2, Brain } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

// Game icons
const GAME_ICONS = {
  poker: "🃏",
  chess: "♔",
  pool: "🎱",
  hangman: "🎯",
  blackjack: "🃏",
  roulette: "🎰"
};

// Rank badges
const getRankBadge = (rank) => {
  if (rank === 1) return { icon: "🥇", color: "text-yellow-500", bg: "bg-yellow-100" };
  if (rank === 2) return { icon: "🥈", color: "text-gray-400", bg: "bg-gray-100" };
  if (rank === 3) return { icon: "🥉", color: "text-amber-600", bg: "bg-amber-100" };
  return { icon: rank.toString(), color: "text-gray-500", bg: "bg-gray-50" };
};

// Role badge
const getRoleBadge = (role) => {
  switch (role) {
    case "elite_vip": return { label: "ELITE", color: "bg-purple-500" };
    case "vip": return { label: "VIP", color: "bg-yellow-500" };
    default: return null;
  }
};

// Leaderboard Entry Component
const LeaderboardEntry = ({ entry, rank, type, onClick }) => {
  const rankBadge = getRankBadge(rank);
  const roleBadge = getRoleBadge(entry.user_role);
  
  return (
    <div 
      onClick={onClick}
      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors ${
        rank <= 3 ? rankBadge.bg : ''
      }`}
    >
      {/* Rank */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
        rank <= 3 ? `${rankBadge.color} text-lg` : 'bg-gray-200 text-gray-600'
      }`}>
        {rank <= 3 ? rankBadge.icon : rank}
      </div>
      
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
        {entry.profile_photo ? (
          <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-white font-bold">{entry.username?.[0]?.toUpperCase()}</span>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="font-bold text-sm truncate">{entry.username}</span>
          {roleBadge && (
            <span className={`${roleBadge.color} text-white text-[8px] px-1 rounded`}>
              {roleBadge.label}
            </span>
          )}
        </div>
        {type === "tournaments" && (
          <div className="text-xs text-gray-500">
            {entry.wins} wins • {entry.total_prize?.toLocaleString()} F-Chips won
          </div>
        )}
        {type === "games" && (
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <span>{GAME_ICONS[entry.game_type]}</span>
            {entry.wins} wins
          </div>
        )}
        {type === "trivia" && (
          <div className="text-xs text-gray-500 flex items-center gap-1" data-testid={`trivia-row-meta-${entry.username}`}>
            <Brain className="w-3 h-3 text-pink-500" />
            {entry.trivia_questions_correct ?? 0} correct • {entry.trivia_credits_earned ?? 0} 🎰 earned
          </div>
        )}
      </div>
      
      {/* Value */}
      <div className="text-right">
        {type === "fchips" && (
          <div className="flex items-center gap-1 text-yellow-600 font-bold">
            <Coins className="w-4 h-4" />
            <span>{entry.fchips_balance?.toLocaleString()}</span>
          </div>
        )}
        {type === "diamonds" && (
          <div className="flex items-center gap-1 text-cyan-500 font-bold">
            <Gem className="w-4 h-4" />
            <span>{entry.diamonds_balance?.toLocaleString()}</span>
          </div>
        )}
        {type === "tournaments" && (
          <div className="flex items-center gap-1 text-purple-600 font-bold">
            <Trophy className="w-4 h-4" />
            <span>{entry.wins}</span>
          </div>
        )}
        {type === "games" && (
          <div className="flex items-center gap-1 text-green-600 font-bold">
            <Medal className="w-4 h-4" />
            <span>{entry.wins}</span>
          </div>
        )}
        {type === "trivia" && (
          <div className="flex items-center gap-1 text-pink-600 font-bold">
            <Brain className="w-4 h-4" />
            <span>{entry.trivia_score?.toLocaleString()} pts</span>
          </div>
        )}
      </div>
    </div>
  );
};

// User Stats Modal
const UserStatsModal = ({ username, isOpen, onClose }) => {
  const { token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && username) {
      setLoading(true);
      axios.get(`${API}/leaderboard/user/${username}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setStats(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, username, token]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : stats ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
                {stats.profile_photo ? (
                  <img src={stats.profile_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-2xl">{stats.username?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div>
                <h3 className="font-bold text-lg">{stats.username}</h3>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  Rank #{stats.fchips_rank}
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-yellow-50 rounded-lg p-3 text-center">
                <Coins className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                <div className="font-bold text-lg">{stats.fchips_balance?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">F-Chips</div>
              </div>
              <div className="bg-cyan-50 rounded-lg p-3 text-center">
                <Gem className="w-6 h-6 text-cyan-500 mx-auto mb-1" />
                <div className="font-bold text-lg">{stats.diamonds_balance?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Diamonds</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <Trophy className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                <div className="font-bold text-lg">{stats.tournament_wins}</div>
                <div className="text-xs text-gray-500">Tournament Wins</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <Medal className="w-6 h-6 text-green-500 mx-auto mb-1" />
                <div className="font-bold text-lg">{stats.total_prize_won?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Prize Money Won</div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500">
              Played in {stats.tournaments_played} tournaments
            </div>

            <button
              onClick={onClose}
              className="w-full mt-4 py-2 bg-gray-200 rounded-lg font-medium hover:bg-gray-300"
            >
              Close
            </button>
          </>
        ) : (
          <div className="text-center py-8 text-red-500">Failed to load stats</div>
        )}
      </div>
    </div>
  );
};

// Main Leaderboard Panel
const LeaderboardPanel = () => {
  const { token, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("fchips"); // fchips, diamonds, tournaments, games
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [gameFilter, setGameFilter] = useState(null);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      // Trivia uses its own endpoint
      let url;
      if (activeTab === "trivia") {
        url = `${API}/trivia/leaderboard`;
      } else {
        url = `${API}/leaderboard/${activeTab}`;
        if (activeTab === "games" && gameFilter) {
          url += `?game_type=${gameFilter}`;
        }
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchLeaderboard();
    }
  }, [token, activeTab, gameFilter]);

  const tabs = [
    { id: "fchips", label: "F-Chips", icon: Coins, color: "text-yellow-500" },
    { id: "diamonds", label: "Diamonds", icon: Gem, color: "text-cyan-500" },
    { id: "tournaments", label: "Tournaments", icon: Trophy, color: "text-purple-500" },
    { id: "games", label: "Games", icon: Gamepad2, color: "text-green-500" },
    { id: "trivia", label: "Trivia", icon: Brain, color: "text-pink-500" },
  ];

  return (
    <div className="retro-window max-h-64 flex flex-col" data-testid="leaderboard-panel">
      <div className="retro-title-bar flex items-center justify-between px-2">
        <span className="flex items-center gap-1">
          <Trophy className="w-4 h-4" />
          <span className="text-xs font-bold">LEADERBOARD</span>
        </span>
        <button onClick={fetchLeaderboard} disabled={loading} className="text-[10px] hover:bg-white/20 px-1 rounded">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="bg-[#C0C0C0] flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-400">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-1.5 text-[10px] font-bold flex items-center justify-center gap-1 ${
                activeTab === tab.id 
                  ? 'bg-white border-t-2 border-purple-500' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <tab.icon className={`w-3 h-3 ${tab.color}`} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Game Filter (for games tab) */}
        {activeTab === "games" && (
          <div className="p-1 bg-gray-200 flex gap-1 overflow-x-auto">
            <button
              onClick={() => setGameFilter(null)}
              className={`px-2 py-0.5 rounded text-[10px] ${!gameFilter ? 'bg-purple-500 text-white' : 'bg-white'}`}
            >
              All
            </button>
            {Object.entries(GAME_ICONS).map(([game, icon]) => (
              <button
                key={game}
                onClick={() => setGameFilter(game)}
                className={`px-2 py-0.5 rounded text-[10px] whitespace-nowrap ${
                  gameFilter === game ? 'bg-purple-500 text-white' : 'bg-white'
                }`}
              >
                {icon} {game}
              </button>
            ))}
          </div>
        )}

        {/* Leaderboard List */}
        <div className="flex-1 overflow-y-auto p-2 retro-scrollbar bg-white">
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading...</div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500 text-sm">No data yet</p>
              <p className="text-gray-400 text-xs">Be the first to top the charts!</p>
            </div>
          ) : (
            <div className="space-y-1">
              {leaderboard.map((entry, index) => (
                <LeaderboardEntry
                  key={`${entry.username}-${index}`}
                  entry={entry}
                  rank={index + 1}
                  type={activeTab}
                  onClick={() => setSelectedUser(entry.username)}
                />
              ))}
            </div>
          )}
        </div>

        {/* My Rank Footer */}
        {user && (
          <div className="p-2 bg-purple-100 border-t border-purple-300">
            <button
              onClick={() => setSelectedUser(user.username)}
              className="w-full text-center text-xs text-purple-700 font-medium hover:underline flex items-center justify-center gap-1"
            >
              <Star className="w-3 h-3" />
              View My Stats
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* User Stats Modal */}
      <UserStatsModal
        username={selectedUser}
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
};

export default LeaderboardPanel;
